import React from 'react';
import { MessageBar, MessageBarType, useTheme } from '@fluentui/react';

// The two halves of "this storefront is busy": the notice that says so, and the wrapper that makes it
// true for whatever form is underneath.
//
// Both take the flag as a prop rather than reading the lease themselves. The pages already hold it
// (the Pages editor folds it into its own edit mode), and a prop keeps these presentational.

interface IStorefrontEditLockNoticeProps {
    locked: boolean;
    // True when the AI assistant is the holder. Every hold is today, but the copy names the assistant
    // only when it is actually the one working, so this never claims something that is not happening.
    heldByAssistant?: boolean;
}

/**
 * The explanation. Plain and temporary on purpose: the user has not lost anything and does not need
 * to do anything, so this is an info bar, not a warning.
 */
export const StorefrontEditLockNotice: React.FC<IStorefrontEditLockNoticeProps> = ({ locked, heldByAssistant }) => {
    const theme = useTheme();
    if (!locked) return null;

    const message = heldByAssistant
        ? 'Adaptive Intelligence is applying changes to this storefront. Editing is paused for a moment.'
        : 'Another editor is applying changes to this storefront. Editing is paused for a moment.';

    return (
        <MessageBar
            messageBarType={MessageBarType.info}
            styles={{
                root: {
                    marginBottom: '1em',
                    backgroundColor: theme.palette.themeLighter,
                    color: theme.palette.neutralPrimary,
                },
                icon: { color: theme.palette.themePrimary },
            }}
        >
            {message}
        </MessageBar>
    );
};

interface IStorefrontReadOnlyRegionProps {
    readOnly: boolean;
    children: React.ReactNode;
}

/**
 * Makes everything inside read-only from one flag.
 *
 * A disabled `fieldset` is what does the work: the HTML spec disables every form control it contains,
 * so a Fluent TextField/Toggle/Dropdown/button anywhere in the subtree stops accepting input and stops
 * taking focus, without every one of them having to be handed a `disabled` prop. `pointerEvents: none`
 * covers the rest: rich text editors are `contenteditable`, not form controls, and a fieldset has no
 * say over those.
 *
 * The reset on the fieldset matters. Its default border/padding would redraw every page, and its UA
 * `min-width: min-content` breaks flex and grid children in ways that are very hard to trace back here.
 */
export const StorefrontReadOnlyRegion: React.FC<IStorefrontReadOnlyRegionProps> = ({ readOnly, children }) => (
    <fieldset
        disabled={readOnly}
        aria-disabled={readOnly}
        style={{
            border: 0,
            margin: 0,
            padding: 0,
            minWidth: 0,
            pointerEvents: readOnly ? 'none' : undefined,
            opacity: readOnly ? 0.6 : undefined,
        }}
    >
        {children}
    </fieldset>
);
